import {
  formatCurrency,
  isPriceHidden,
  isProductNegotiable,
} from "./utilityFunctions";

// Offer rules and copy, mirroring the mobile app's utils/negotiation.js. The
// server re-checks everything (including a fixed-price product's hidden
// floor, which only it knows).

export const OFFER_PAYMENT_HINT =
  "The seller can accept, counter, or decline. Don't pay until you both agree.";

export const FULL_PRICE_OFFER_NOTE =
  "That's the listed price, so this will be a regular order with no offer.";

// "12,500.50" -> 1250050. Returns null for empty or invalid input.
export const parseMoneyToCents = (text) => {
  if (text == null) return null;
  const normalized = String(text).replace(/,/g, "").trim();
  if (!normalized) return null;
  const value = Number(normalized);
  if (!Number.isFinite(value) || value <= 0) return null;
  return Math.round(value * 100);
};

// "17000.5" -> "17,000.5" as the user types: digits grouped with commas, at
// most one decimal point and 2 decimal places. parseMoneyToCents strips the
// commas again.
export const formatMoneyInput = (text) => {
  const cleaned = String(text ?? "").replace(/[^0-9.]/g, "");
  if (!cleaned) return "";
  const [integerRaw, ...decimals] = cleaned.split(".");
  const grouped = integerRaw
    .replace(/^0+(?=\d)/, "")
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  if (!decimals.length) return grouped;
  return `${grouped || "0"}.${decimals.join("").slice(0, 2)}`;
};

// 1250050 -> "12,500.5" for prefilling an input.
export const centsToInput = (cents) =>
  cents == null
    ? ""
    : formatMoneyInput(String(Number((cents / 100).toFixed(2))));

export const formatCents = (cents, currency) =>
  formatCurrency(cents / 100, currency);

// Null for a hidden price (OFFERS) product: buyers never get its price.
const listedPriceCents = (product) =>
  isPriceHidden(product)
    ? null
    : (product?.priceCents ?? Math.round((Number(product?.price) || 0) * 100));

// A price range's advertised minimum, which is also its offer floor.
const rangeMinCents = (product) => {
  if (product?.pricingMode !== "RANGE") return null;
  if (product.minPriceCents != null) return product.minPriceCents;
  return product.minPrice != null
    ? Math.round(Number(product.minPrice) * 100)
    : null;
};

// An offer at the full listed price (a range's max) isn't a negotiation:
// it's sent as no offer, and the order is a regular one at that price.
// (Never true for a hidden price product: the server handles that case.)
export const isFullPriceOffer = (product, cents) =>
  cents != null &&
  listedPriceCents(product) != null &&
  cents >= listedPriceCents(product);

// Returns an error message, or null when the offer can be sent.
export const validateOfferCents = (product, cents) => {
  if (!isProductNegotiable(product)) return "This item isn't open to offers.";
  if (cents == null) return "Enter your offer.";
  if (listedPriceCents(product) != null && cents > listedPriceCents(product)) {
    return `Your offer can't be more than the listed price (${formatCents(listedPriceCents(product), product?.currency)}).`;
  }
  const floor = rangeMinCents(product);
  if (floor != null && cents < floor) {
    return `Your offer must be at least ${formatCents(floor, product?.currency)}, the lowest price in the range.`;
  }
  return null;
};

// Helper text under an offer input: what's listed and the allowed offers.
export const offerCeilingHint = (product) => {
  if (isPriceHidden(product)) {
    return "The seller hasn't shown a price. If you offer more than their price, you'll pay their price.";
  }
  const max = formatCents(listedPriceCents(product), product?.currency);
  const floor = rangeMinCents(product);
  if (floor != null) {
    const min = formatCents(floor, product?.currency);
    return `Listed at ${min} – ${max}. Offer from ${min} up to ${max}.`;
  }
  return `Listed at ${max}. Offer up to that.`;
};
